// ============================================
// Trạng thái tồn kho DÙNG CHUNG cho store-admin.
//
// Bug #181: dropdown Status ở trang Inventory chọn "Low Stock" / "Out of Stock"
// ra bảng rỗng. Nguyên nhân là mỗi chỗ tự suy diễn trạng thái theo cách riêng:
//   - mapper của InventoryPage đọc ngưỡng ở đúng MỘT tên field
//     (`min_stock_threshold`); payload dùng tên khác -> ngưỡng = 0 -> điều kiện
//     LOW_STOCK (0 < tồn <= ngưỡng) không bao giờ đúng -> lọc "Low Stock" rỗng;
//   - badge trạng thái ở InventoryTableRow lại mặc định OUT_OF_STOCK khi không
//     đọc được status -> dòng còn hàng vẫn bị tô "hết hàng";
//   - ô số lượng tô đỏ theo tỷ lệ available/max, một định nghĩa "sắp hết" thứ ba.
//
// Mọi nơi cần biết một dòng tồn kho đang IN/LOW/OUT PHẢI đi qua file này.
//
// CÔNG THỨC ĐỔI 09/09/2026 (thông báo BE): ngưỡng KHÔNG còn nhập tay nữa mà tính
// tự động theo mốc tồn sau LẦN NHẬP HÀNG GẦN NHẤT (`last_restock_stock` = 100%):
//   OUT_OF_STOCK: current = 0
//   LOW_STOCK:    0 < current < 20% mốc nhập gần nhất
//   IN_STOCK:     current >= 20% mốc nhập gần nhất
//
// Vẫn tính trên tồn kho HIỆN CÓ chứ không phải available = current - reserved:
// giữ chỗ không làm hàng biến mất khỏi kho.
//
// Hai điểm phải để ý khi đọc code bên dưới:
//   1. Mốc 20% là ngưỡng ĐỂ SO SÁNH, và biên thuộc về IN_STOCK — đúng 20% là
//      còn hàng. Bản trước dùng `<=` (ngưỡng nhập tay tính là "đã thấp"), giữ
//      nguyên dấu đó thì mọi dòng đứng chẵn 20% sẽ lệch nhãn so với BE.
//   2. `min_threshold` / `min_stock_threshold` trong response TỪ NAY LÀ SỐ TÍNH
//      SẴN (= 20% mốc), chỉ để hiển thị. Không được gửi ngược lên:
//      PATCH /inventory/{id}/ kèm `min_stock_threshold` trả 400.
// ============================================

export const STOCK_STATUSES = ['IN_STOCK', 'LOW_STOCK', 'OUT_OF_STOCK'] as const;

export type StockStatusValue = (typeof STOCK_STATUSES)[number];

/**
 * Tên field chứa TỒN KHO HIỆN CÓ, theo thứ tự ưu tiên.
 * `/inventory/sellable-products/` trả `current_stock`, `/inventory/` trả
 * `quantity` (xem docs/API_REFERENCE.md#List-Inventory).
 */
const QUANTITY_KEYS = [
  'current_stock',
  'quantity',
  'current_quantity',
  'stock_quantity',
  'on_hand',
  'on_hand_quantity',
] as const;

/**
 * Tên field chứa NGƯỠNG CẢNH BÁO (từ 09/09/2026 là số BE tính sẵn = 20% mốc
 * nhập gần nhất), theo thứ tự ưu tiên.
 *
 * Đừng rút gọn danh sách này về một tên duy nhất: mỗi endpoint gọi một kiểu
 * (`min_stock_threshold` ở sellable-products, `min_threshold` ở low-stock,
 * `min_quantity` ở /inventory/). Đọc hụt ngưỡng thì ngưỡng thành 0 và trạng thái
 * LOW_STOCK biến mất hoàn toàn — chính là bug #181.
 */
const MIN_THRESHOLD_KEYS = [
  'min_stock_threshold',
  'min_threshold',
  'min_quantity',
  'min_stock',
  'min_stock_level',
  'low_stock_threshold',
  'reorder_point',
  'reorder_level',
] as const;

/** Tên field chứa trạng thái do BE tính sẵn. */
const STATUS_KEYS = ['stock_status', 'status', 'inventory_status'] as const;

/**
 * Mốc 100% — tồn ngay sau lần nhập hàng gần nhất (BE thêm 09/09/2026).
 * `null` ở hàng CHƯA TỪNG NHẬP: lúc đó không có gì để lấy 20%, và một món chưa
 * từng nhập thì không thể gọi là "sắp hết" — chỉ còn OUT_OF_STOCK khi tồn = 0.
 */
const LAST_RESTOCK_KEYS = ['last_restock_stock', 'last_restock_quantity'] as const;

/** Tỷ lệ chốt trạng thái LOW_STOCK: dưới 20% mốc nhập gần nhất. */
export const LOW_STOCK_RATIO = 0.2;

/**
 * Biến thể chuỗi được chấp nhận -> trạng thái chuẩn.
 *
 * So khớp TUYỆT ĐỐI sau khi chuẩn hoá, cố ý không dùng `includes`: field `status`
 * của SẢN PHẨM ("ACTIVE"/"DRAFT"/"INACTIVE") hay lọt vào cùng chỗ với
 * `stock_status`, khớp mờ là xếp nhầm nó thành trạng thái tồn kho.
 */
const STATUS_ALIASES: Record<string, StockStatusValue> = {
  IN_STOCK: 'IN_STOCK',
  INSTOCK: 'IN_STOCK',
  IN: 'IN_STOCK',
  AVAILABLE: 'IN_STOCK',
  LOW_STOCK: 'LOW_STOCK',
  LOWSTOCK: 'LOW_STOCK',
  LOW: 'LOW_STOCK',
  OUT_OF_STOCK: 'OUT_OF_STOCK',
  OUTOFSTOCK: 'OUT_OF_STOCK',
  OUT_OF_STOCKS: 'OUT_OF_STOCK',
  OUT: 'OUT_OF_STOCK',
  SOLD_OUT: 'OUT_OF_STOCK',
  SOLDOUT: 'OUT_OF_STOCK',
};

/** Số an toàn: tồn kho là Decimal (hàng cân ký) nên nhận cả chuỗi "10.000". */
export function toStockNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value.trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

/**
 * Chuẩn hoá một chuỗi trạng thái bất kỳ về 3 giá trị chuẩn.
 * Không nhận ra thì trả `null` để phía gọi rơi xuống nhánh suy diễn.
 */
export function normalizeStockStatus(raw: unknown): StockStatusValue | null {
  if (typeof raw !== 'string') return null;

  const normalized = raw
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

  return STATUS_ALIASES[normalized] ?? null;
}

/**
 * Giá trị chọn trong dropdown -> trạng thái chuẩn.
 * "all" / rỗng / giá trị lạ đều trả `null` = không lọc.
 */
export function normalizeStockStatusFilter(raw: unknown): StockStatusValue | null {
  if (typeof raw !== 'string') return null;
  if (raw.trim().toLowerCase() === 'all') return null;
  return normalizeStockStatus(raw);
}

/**
 * Giá trị lọc -> GIÁ TRỊ GỬI LÊN SERVER cho query param `stock_status` của
 * `/inventory/sellable-products/`.
 *
 * BE chỉ nhận đúng `IN_STOCK` / `LOW_STOCK` / `OUT_OF_STOCK` và **trả 400 khi
 * gặp giá trị lạ** (chứ không im lặng trả rỗng như trước). Vì vậy MỌI thứ đi
 * lên param này phải qua đây, và hàm chỉ có hai đầu ra:
 *   - một phần tử của `STOCK_STATUSES` (whitelist cứng), hoặc
 *   - `null` — nghĩa là "KHÔNG GỬI PARAM" (bỏ hẳn khỏi query), không phải gửi
 *     chuỗi rỗng và cũng không phải gửi cả 3 giá trị.
 *
 * Nhãn đã dịch ("Mało na stanie", "Замало"), "all", `undefined`, số, object...
 * đều rơi vào nhánh `null`. Kiểu trả về là union literal nên không thể lọt một
 * chuỗi tuỳ ý ra ngoài mà TypeScript không kêu.
 */
export function toStockStatusParam(raw: unknown): StockStatusValue | null {
  const status = normalizeStockStatusFilter(raw);
  // Kiểm tra lại với whitelist ở RUNTIME: nếu sau này có ai thêm alias trỏ tới
  // một giá trị BE không biết, request vẫn không mang giá trị đó đi.
  return status !== null && STOCK_STATUSES.includes(status) ? status : null;
}

/**
 * Suy diễn trạng thái từ số lượng + ngưỡng (ngưỡng = 20% mốc nhập gần nhất, do
 * BE tính sẵn và gửi kèm; xem `readMinThreshold` khi payload chỉ có mốc 100%).
 *
 * Dấu `<` chứ không phải `<=`: theo công thức mới, đúng 20% vẫn là CÒN HÀNG.
 * Ngưỡng = 0 (hàng chưa từng nhập, `last_restock_stock` null) thì không có
 * vùng "sắp hết" — chỉ hết hàng khi tồn về 0.
 */
export function deriveStockStatus(quantity: number, minThreshold: number): StockStatusValue {
  const current = toStockNumber(quantity, 0);
  const min = toStockNumber(minThreshold, 0);

  if (current <= 0) return 'OUT_OF_STOCK';
  if (min > 0 && current < min) return 'LOW_STOCK';
  return 'IN_STOCK';
}

/**
 * Trạng thái cuối cùng của một dòng: ưu tiên giá trị BE tính sẵn, thiếu/không
 * hợp lệ thì suy diễn theo đúng công thức của BE.
 */
export function resolveStockStatus(
  rawStatus: unknown,
  quantity: number,
  minThreshold: number
): StockStatusValue {
  return normalizeStockStatus(rawStatus) ?? deriveStockStatus(quantity, minThreshold);
}

function readFirst(record: Record<string, unknown> | null | undefined, keys: readonly string[]): unknown {
  if (!record) return undefined;
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
}

/** Tồn kho hiện có của một record thô từ API. */
export function readStockQuantity(record: Record<string, unknown> | null | undefined): number {
  return toStockNumber(readFirst(record, QUANTITY_KEYS), 0);
}

/**
 * Mốc 100% (tồn sau lần nhập gần nhất) của một record thô từ API.
 * Trả `0` khi hàng chưa từng nhập hoặc BE chưa gửi field.
 */
export function readLastRestockStock(
  record: Record<string, unknown> | null | undefined,
  ...fallbackRecords: Array<Record<string, unknown> | null | undefined>
): number {
  const direct = readFirst(record, LAST_RESTOCK_KEYS);
  if (direct !== undefined) return toStockNumber(direct, 0);

  for (const fallback of fallbackRecords) {
    const value = readFirst(fallback, LAST_RESTOCK_KEYS);
    if (value !== undefined) return toStockNumber(value, 0);
  }
  return 0;
}

/**
 * Ngưỡng cảnh báo của một record thô từ API — tức mốc 20%.
 * `fallbackRecords` để dò tiếp ở object lồng (`product`, `inventory`) khi dòng
 * tồn kho không tự mang ngưỡng.
 *
 * Ưu tiên số BE tính sẵn (`min_threshold`/`min_stock_threshold`); chỉ khi cả ba
 * record đều không có mới tự lấy 20% của `last_restock_stock`. Nhánh tự tính là
 * dự phòng cho endpoint chưa kịp thêm field, KHÔNG phải chỗ định nghĩa công
 * thức — số của BE luôn thắng để bảng và bộ lọc (lọc ở server) không lệch nhau.
 */
export function readMinThreshold(
  record: Record<string, unknown> | null | undefined,
  ...fallbackRecords: Array<Record<string, unknown> | null | undefined>
): number {
  const direct = readFirst(record, MIN_THRESHOLD_KEYS);
  if (direct !== undefined) return toStockNumber(direct, 0);

  for (const fallback of fallbackRecords) {
    const value = readFirst(fallback, MIN_THRESHOLD_KEYS);
    if (value !== undefined) return toStockNumber(value, 0);
  }

  const lastRestock = readLastRestockStock(record, ...fallbackRecords);
  return lastRestock > 0 ? lastRestock * LOW_STOCK_RATIO : 0;
}

/** Trạng thái của một record thô từ API — đọc field + suy diễn trong một bước. */
export function readStockStatus(
  record: Record<string, unknown> | null | undefined,
  ...fallbackRecords: Array<Record<string, unknown> | null | undefined>
): StockStatusValue {
  const quantity = readStockQuantity(record);
  const minThreshold = readMinThreshold(record, ...fallbackRecords);
  return resolveStockStatus(readFirst(record, STATUS_KEYS), quantity, minThreshold);
}

/**
 * Một dòng có khớp lựa chọn của dropdown không. `null` = "All Status" -> khớp hết.
 *
 * Trang Inventory KHÔNG dùng hàm này nữa (lọc đã chuyển xuống server qua
 * `stock_status`). Giữ lại cho những chỗ chỉ có dữ liệu trong tay và không gọi
 * lại API được; đừng dùng nó để lọc lần hai lên tập đã được server lọc.
 */
export function matchesStockStatus(
  status: StockStatusValue,
  filter: StockStatusValue | null
): boolean {
  return filter === null || status === filter;
}

/** Key i18n tương ứng, để nhãn ở badge và ở dropdown luôn cùng một chuỗi. */
export const STOCK_STATUS_I18N_KEYS: Record<StockStatusValue, string> = {
  IN_STOCK: 'inStock',
  LOW_STOCK: 'lowStock',
  OUT_OF_STOCK: 'outOfStock',
};
